import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { KeyRound, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForcePasswordReset = () => {
  const { user, logout, updateUser, tempPassword } = useAuth();
  const needsTemp = !tempPassword; // page was reloaded on this screen — ask for temp password
  const [tempPw, setTempPw] = useState('');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const currentPassword = tempPassword || tempPw;
    if (needsTemp && !tempPw) { setError('Please enter the temporary password you were given'); return; }
    if (pw.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (pw !== confirm) { setError('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/force-set-password`, {
        email: user.email,
        current_password: currentPassword,
        new_password: pw,
      });
      updateUser({ must_reset_password: false });
      toast.success('Password set — welcome!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not set password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="force-reset-screen">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
        <div className="w-12 h-12 rounded-xl bg-[#34657f]/10 flex items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6 text-[#34657f]" />
        </div>
        <h1 className="font-['Manrope'] text-2xl font-bold text-[#1A1A1A]">Set your password</h1>
        <p className="text-[#5C5C5C] mt-1 text-sm">
          Welcome, {user?.name?.split(' ')[0] || 'there'}! For your security, please replace the temporary password with one of your own before continuing.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {needsTemp && (
            <div className="space-y-1.5">
              <Label htmlFor="temp-pw">Temporary password</Label>
              <Input id="temp-pw" type="password" value={tempPw} onChange={(e) => setTempPw(e.target.value)} placeholder="The password you were given" data-testid="force-reset-temp" autoFocus />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="new-pw">New password</Label>
            <Input id="new-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" data-testid="force-reset-password" autoFocus={!needsTemp} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pw">Confirm password</Label>
            <Input id="confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your new password" data-testid="force-reset-confirm" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="force-reset-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#34657f] hover:bg-[#2a5060]" disabled={saving} data-testid="force-reset-submit">
            <KeyRound className="w-4 h-4 mr-2" />
            {saving ? 'Saving…' : 'Set password & continue'}
          </Button>
        </form>

        <button onClick={logout} className="mt-4 text-xs text-[#5C5C5C] hover:underline w-full text-center" data-testid="force-reset-logout">
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ForcePasswordReset;
